// Deferred rendering pipeline — G-buffer management + geometry passes
// Engine code: no game knowledge. Game submits draw calls, engine handles the rest.

import { DEPTH_FORMAT, createRenderTarget, type GPU } from "./gpu.js";

// G-buffer shader: supports instanced, static (per-vertex color), and ground geometry
export const GBUFFER_SHADER = /* wgsl */ `
struct Uniforms { viewProj: mat4x4f };
@group(0) @binding(0) var<uniform> uniforms: Uniforms;

struct VSOut {
  @builtin(position) clipPos: vec4f,
  @location(0) worldPos: vec3f,
  @location(1) normal: vec3f,
  @location(2) color: vec3f,
  @location(3) emissive: f32,
  @location(4) roughness: f32,
};
struct GBufferOut {
  @location(0) albedo: vec4f,   // rgb = color, a = emissive intensity
  @location(1) normal: vec4f,
};

// Instanced geometry (position + normal vertex, instance data)
struct Instance {
  @location(6) iPos: vec3f,
  @location(7) iScaleX: f32,
  @location(8) iScaleYZ: vec2f,
  @location(9) iColor: vec3f,
  @location(10) iEmissive: f32,
  @location(11) iRoughness: f32,
};

@vertex fn vsInstanced(@location(0) vp: vec3f, @location(1) vn: vec3f, inst: Instance) -> VSOut {
  let scale = vec3f(inst.iScaleX, inst.iScaleYZ.x, inst.iScaleYZ.y);
  let wp = inst.iPos + vp * scale;
  var o: VSOut; o.clipPos = uniforms.viewProj * vec4f(wp, 1.0); o.worldPos = wp; o.normal = vn; o.color = inst.iColor; o.emissive = inst.iEmissive; o.roughness = inst.iRoughness; return o;
}

// Static geometry with per-vertex color
@vertex fn vsStatic(@location(0) vp: vec3f, @location(1) vn: vec3f, @location(2) vc: vec3f) -> VSOut {
  var o: VSOut; o.clipPos = uniforms.viewProj * vec4f(vp, 1.0); o.worldPos = vp; o.normal = vn; o.color = vc; o.emissive = 0.0; o.roughness = 0.9; return o;
}

// Ground (hardcoded color, no per-vertex color buffer needed)
@vertex fn vsGround(@location(0) vp: vec3f, @location(1) vn: vec3f) -> VSOut {
  var o: VSOut; o.clipPos = uniforms.viewProj * vec4f(vp, 1.0); o.worldPos = vp; o.normal = vn; o.color = vec3f(0.15, 0.15, 0.18); o.emissive = 0.0; o.roughness = 0.9; return o;
}

// Procedural grid: thin lines every 1 unit, brighter lines every 4 units
fn gridLine(coord: f32, width: f32) -> f32 {
  let d = abs(fract(coord - 0.5) - 0.5);
  let fw = fwidth(d);                       // ~1 pixel in screen space
  return 1.0 - smoothstep(width - fw, width + fw, d);
}

fn gridPattern(wp: vec3f, N: vec3f) -> f32 {
  let absN = abs(N);
  var uv: vec2f;
  if (absN.y > absN.x && absN.y > absN.z) {
    uv = wp.xz;
  } else if (absN.x > absN.z) {
    uv = wp.yz;
  } else {
    uv = wp.xy;
  }

  // Small grid (every 1 unit) — thin dark lines
  let small = max(gridLine(uv.x, 0.01), gridLine(uv.y, 0.01));
  // Major grid (every 4 units)
  let major = max(gridLine(uv.x * 0.25, 0.008), gridLine(uv.y * 0.25, 0.008));

  return max(small, major);
}

@fragment fn fsGeo(in: VSOut) -> GBufferOut {
  var o: GBufferOut;
  let N = normalize(in.normal);
  let grid = gridPattern(in.worldPos, N);
  let color = in.color * (1.0 - grid * 0.35);
  o.albedo = vec4f(color, in.emissive);
  o.normal = vec4f(N, in.roughness);
  return o;
}
`;

export const GBUFFER_TARGETS: GPUColorTargetState[] = [
  { format: "rgba16float" },  // albedo (16f to support emissive > 1.0)
  { format: "rgba16float" },  // normal
];

export const POS_NORM_LAYOUT: GPUVertexBufferLayout = {
  arrayStride: 24, stepMode: "vertex",
  attributes: [
    { shaderLocation: 0, offset: 0, format: "float32x3" },
    { shaderLocation: 1, offset: 12, format: "float32x3" },
  ],
};

export const INSTANCE_STRIDE = 44; // pos(3) + scale(3) + color(3) + emissive(1) + roughness(1) = 11 floats = 44 bytes

export const INSTANCE_LAYOUT: GPUVertexBufferLayout = {
  arrayStride: INSTANCE_STRIDE, stepMode: "instance",
  attributes: [
    { shaderLocation: 6, offset: 0, format: "float32x3" },   // pos
    { shaderLocation: 7, offset: 12, format: "float32" },     // scaleX
    { shaderLocation: 8, offset: 16, format: "float32x2" },   // scaleYZ
    { shaderLocation: 9, offset: 24, format: "float32x3" },   // color
    { shaderLocation: 10, offset: 36, format: "float32" },    // emissive
    { shaderLocation: 11, offset: 40, format: "float32" },    // roughness
  ],
};

export const VERTEX_COLOR_LAYOUT: GPUVertexBufferLayout = {
  arrayStride: 12, stepMode: "vertex",
  attributes: [{ shaderLocation: 2, offset: 0, format: "float32x3" }],
};

export type GBuffer = {
  albedo: GPUTexture;
  normal: GPUTexture;
};

export class DeferredPipeline {
  gBuffer: GBuffer;
  instancedPipeline: GPURenderPipeline;
  staticPipeline: GPURenderPipeline;
  groundPipeline: GPURenderPipeline;
  uniformBuffer: GPUBuffer;
  bindGroup: GPUBindGroup;

  private width: number;
  private height: number;

  constructor(private gpu: GPU) {
    const { device, format, canvas } = gpu;
    this.width = canvas.width;
    this.height = canvas.height;

    this.gBuffer = {
      albedo: createRenderTarget(device, this.width, this.height, "rgba16float"),
      normal: createRenderTarget(device, this.width, this.height, "rgba16float"),
    };

    const module = device.createShaderModule({ code: GBUFFER_SHADER });
    const uniformLayout = device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: "uniform" } }],
    });
    const pipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [uniformLayout] });
    const depthStencil: GPUDepthStencilState = { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: "less" };

    this.instancedPipeline = device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module, entryPoint: "vsInstanced", buffers: [POS_NORM_LAYOUT, INSTANCE_LAYOUT] },
      fragment: { module, entryPoint: "fsGeo", targets: GBUFFER_TARGETS },
      primitive: { topology: "triangle-list", cullMode: "back" },
      depthStencil,
    });

    this.groundPipeline = device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module, entryPoint: "vsGround", buffers: [POS_NORM_LAYOUT] },
      fragment: { module, entryPoint: "fsGeo", targets: GBUFFER_TARGETS },
      primitive: { topology: "triangle-list" },
      depthStencil,
    });

    this.staticPipeline = device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module, entryPoint: "vsStatic", buffers: [POS_NORM_LAYOUT, VERTEX_COLOR_LAYOUT] },
      fragment: { module, entryPoint: "fsGeo", targets: GBUFFER_TARGETS },
      primitive: { topology: "triangle-list" },
      depthStencil,
    });

    this.uniformBuffer = device.createBuffer({ size: 64, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    this.bindGroup = device.createBindGroup({
      layout: uniformLayout,
      entries: [{ binding: 0, resource: { buffer: this.uniformBuffer } }],
    });
  }

  setViewProj(viewProj: Float32Array): void {
    this.gpu.device.queue.writeBuffer(this.uniformBuffer, 0, viewProj);
  }

  beginPass(encoder: GPUCommandEncoder): GPURenderPassEncoder {
    return encoder.beginRenderPass({
      colorAttachments: [
        { view: this.gBuffer.albedo.createView(), clearValue: { r: 0, g: 0, b: 0, a: 0 }, loadOp: "clear", storeOp: "store" },
        { view: this.gBuffer.normal.createView(), clearValue: { r: 0, g: 0, b: 0, a: 0 }, loadOp: "clear", storeOp: "store" },
      ],
      depthStencilAttachment: {
        view: this.gpu.depthTexture.createView(),
        depthClearValue: 1.0, depthLoadOp: "clear", depthStoreOp: "store",
      },
    });
  }

  /** Begin a pass that loads existing G-buffer + depth (for ocean overlay after opaque lighting) */
  beginLoadPass(encoder: GPUCommandEncoder): GPURenderPassEncoder {
    return encoder.beginRenderPass({
      colorAttachments: [
        { view: this.gBuffer.albedo.createView(), loadOp: "load", storeOp: "store" },
        { view: this.gBuffer.normal.createView(), loadOp: "load", storeOp: "store" },
      ],
      depthStencilAttachment: {
        view: this.gpu.depthTexture.createView(),
        depthLoadOp: "load", depthStoreOp: "store",
      },
    });
  }
}
